import artistsData from "@/content/artists.json";
import { useT, type Lang } from "@/lib/i18n";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";
import { SECTION_CLS } from "./Releases";

export interface Artist {
  id: string;
  name: string;
  role?: "virtual" | "recording";
  roleLabel?: string;
  roleLabel_en?: string;
  roleLabel_tr?: string;
  genre?: string;
  genre_en?: string;
  genre_tr?: string;
  subgenre?: string;
  subgenre_en?: string;
  subgenre_tr?: string;
  character?: string;
  image: string;
  alt?: string;
  bio_en?: string;
  bio_tr?: string;
  debut?: boolean;
  currentRelease?: string;
}

export const Artists = ({
  onApply,
  lang,
}: {
  onApply: () => void;
  lang: Lang;
}) => {
  const t = useT();
  const items = artistsData.artists as Artist[];

  return (
    <section id="artists" data-testid="artists-section" className={SECTION_CLS}>
      <SectionHeading
        index={t.artists.index}
        eyebrow={t.artists.eyebrow}
        title={t.artists.title}
        testId="artists-heading"
      />
      {items.length === 0 ? (
        <Reveal>
          <div
            data-testid="artists-pending"
            className="flex flex-col gap-10 border border-if-line bg-if-surface/40 p-10 md:flex-row md:items-end md:justify-between md:p-16"
          >
            <p className="max-w-xl text-base font-light leading-relaxed text-if-dim">
              {t.artists.pending}
            </p>
            <button
              data-testid="artists-apply-cta"
              onClick={onApply}
              className="shrink-0 border border-if-gold/50 px-8 py-3 text-[11px] uppercase tracking-[0.25em] text-if-gold transition-colors duration-300 hover:bg-if-gold hover:text-if-black"
            >
              {t.artists.applyCta}
            </button>
          </div>
        </Reveal>
      ) : (
        <div className="space-y-12">
          {items.map((a) => (
            <Reveal key={a.id}>
              <article
                data-testid={`artist-card-${a.id}`}
                className="grid items-start gap-10 border border-if-line bg-if-surface/30 p-8 lg:grid-cols-[1.28fr_1fr] lg:gap-12 lg:px-12 lg:py-10"
              >
                <div className="mx-auto flex aspect-[6/5] w-full max-w-2xl items-center justify-center">
                  <img
                    src={a.image}
                    alt={a.alt ?? `${a.name} — Istanbul Frequency`}
                    data-testid={`artist-image-${a.id}`}
                    draggable={false}
                    className="max-h-full w-auto max-w-full select-none object-contain"
                  />
                </div>
                <div className="self-start">
                  <h3
                    data-testid={`artist-name-${a.id}`}
                    className="font-serif text-4xl font-light tracking-tight text-if-text sm:text-5xl"
                  >
                    {a.name}
                  </h3>
                  {a.role && (
                    <p className="mt-6 text-[11px] uppercase tracking-[0.3em] text-if-gold">
                      {a.roleLabel ??
                        (a.roleLabel_en || a.roleLabel_tr
                          ? lang === "tr"
                            ? a.roleLabel_tr
                            : a.roleLabel_en
                          : a.role === "virtual"
                            ? t.artists.virtualArtist
                            : t.artists.recordingArtist)}
                    </p>
                  )}
                  {(a.genre || a.genre_en || a.genre_tr) && (
                    <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-if-dim">
                      {a.genre ?? (lang === "tr" ? a.genre_tr : a.genre_en)}
                    </p>
                  )}
                  {(a.subgenre || a.subgenre_en || a.subgenre_tr) && (
                    <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-if-dim/80">
                      {a.subgenre ?? (lang === "tr" ? a.subgenre_tr : a.subgenre_en)}
                    </p>
                  )}
                  {a.character && (
                    <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-if-dim/60">
                      {a.character}
                    </p>
                  )}
                  {(a.debut || a.currentRelease) && (
                    <div className="mt-8 flex flex-wrap gap-3">
                      {a.debut && (
                        <p
                          data-testid={`artist-status-${a.id}`}
                          className="inline-block border border-if-gold/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-if-gold"
                        >
                          {t.artists.debut}
                        </p>
                      )}
                      {a.currentRelease && (
                        <p
                          data-testid={`artist-release-${a.id}`}
                          className="inline-block border border-if-gold/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-if-gold"
                        >
                          {t.artists.currentRelease} — {a.currentRelease}
                        </p>
                      )}
                    </div>
                  )}
                  {(a.bio_en || a.bio_tr) && (
                    <p className="mt-8 max-w-md text-sm font-light leading-relaxed text-if-dim">
                      {lang === "tr" ? a.bio_tr : a.bio_en}
                    </p>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
          <Reveal>
            <div className="flex justify-center md:justify-end">
              <button
                data-testid="artists-apply-cta"
                onClick={onApply}
                className="border border-if-gold/50 px-8 py-3 text-[11px] uppercase tracking-[0.25em] text-if-gold transition-colors duration-300 hover:bg-if-gold hover:text-if-black"
              >
                {t.artists.applyCta}
              </button>
            </div>
          </Reveal>
        </div>
      )}
    </section>
  );
};
